import React, { useContext } from "react";
import { Field } from "formik";
import get from "lodash.get";

import { getCompanyDetail, suggestCompany } from "../../../api/cybwell";
import { useMessageSource } from "react-message-source";
import { ErrorContext } from "../../../route/error/provider";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import CompanyAutocomplete from "../../shared/components/autocomplete/company-autocomplete";
import { CompanyModel } from "../types";
import { formatFullStreet } from "../../../util/format";
import { FieldProps } from "formik/dist/Field";

/**
 * The Company Select Component.
 */
export default function CompanySelect({
  selectedCompany,
  setSelectedCompany,
}: {
  selectedCompany?: CompanyModel;
  setSelectedCompany: (selectedCompany: CompanyModel) => void;
}): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { setError } = useContext(ErrorContext);

  /**
   * Triggers a company search.
   * @param searchTerm the search string.
   */
  const doSearchCompany = async (searchTerm: string) => {
    if (searchTerm && searchTerm.length > 0) {
      try {
        return (await suggestCompany(searchTerm)) as CompanyModel[];
      } catch (error) {
        setError(error);
      }
    }
    return [];
  };
  /**
   * Adds the detailed data to a company that is defined with the ehraid
   * @param company the company.
   */
  const doViewCompanyDetails = async (
    company: Awaited<ReturnType<typeof doSearchCompany>>[0],
  ) => {
    if (company && company.ehraid) {
      try {
        const companyDetailData = await getCompanyDetail(company.ehraid);
        const companyModel: CompanyModel = {
          ...companyDetailData,
          fullStreet: formatFullStreet(companyDetailData.address),
          isCompanyManuallyEntered: false,
          industryBranchCode: null,
          industrySubBranchCode: null,
        };
        setSelectedCompany(companyModel);
      } catch (error) {
        setError(error);
      }
    } else {
      setSelectedCompany(null);
    }
  };

  return (
    <InputContainer>
      <Field name="selectedCompany" key="selectedCompany">
        {({ form, field }: FieldProps) => {
          const error = get(form, `errors.${field.name}`);
          const touched = get(form, `touched.${field.name}`);

          return (
            <CompanyAutocomplete
              id={field.name}
              value={field.value}
              error={!!error && !!touched}
              helperText={(touched && error) as string}
              label={getMessage("Company.select")}
              name="selectedCompany"
              selected={selectedCompany}
              doSearch={doSearchCompany}
              onSelect={doViewCompanyDetails}
              onBlur={field.onBlur}
              itemToSuggestion={(item) =>
                item
                  ? `${item.name}, ${item.address.street} ${item.address.streetNr}, ${item.address.zipCode} ${item.address.city}`
                  : ""
              }
            />
          );
        }}
      </Field>
    </InputContainer>
  );
}
